import React from 'react';
import moment from 'moment';
import { GENDERS, US_STATES, capitalize } from 'utils/utils';
import { visitTypes } from 'context/visitContext';
import { uniq } from 'ramda';
import { useAppContext } from 'context/appContext';
import Icon, { IconSprite } from '../Icon/Icon';
import Input from '../Inputs/Input/Input';
import SelectInput from '../Inputs/SelectInput/SelectInput';

type FieldType = 'name' | 'provider' | 'state' | 'gender' | 'month' | 'visit';

export type FilterType = {
  name: string;
  state: string;
  month: string;
  gender: string;
  visit: string;
  provider: string;
};

function SearchFilter({
  onSearchViewClose,
  onFilter,
  fields = ['name', 'gender', 'state'],
}: {
  onSearchViewClose: () => void;
  onFilter: (search: FilterType) => void;
  fields?: FieldType[];
}) {
  const [{ patients }] = useAppContext();
  const [search, setSearch] = React.useState<FilterType>({
    name: '',
    state: '',
    month: '',
    gender: '',
    visit: '',
    provider: '',
  });

  const providers = React.useMemo(
    () =>
      uniq(
        patients
          .filter(fndPatient => Boolean(fndPatient.assignedProvider))
          .map(fndPatient => ({
            value: `${fndPatient.assignedProvider.firstName} ${fndPatient.assignedProvider.lastName}`,
            label: `${fndPatient.assignedProvider.firstName} ${fndPatient.assignedProvider.lastName}`,
          })),
      ),
    [patients],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    const searchValues = {
      ...search,
      [name]: value,
    };
    setSearch(searchValues);
    onFilter(searchValues);
  };

  const handleSearchViewClose = () => {
    const resetSearch = Object.fromEntries(
      Object.entries(search).map(fndSearch => [fndSearch[0], '']),
    ) as FilterType;
    setSearch(resetSearch);
    onFilter(resetSearch);
    onSearchViewClose();
  };

  return (
    <div className="flex flex-row space-x-2 items-center w-full">
      {fields.includes('name') && (
        <div className="relative flex-1 rounded-full">
          <Icon
            id="iconSearch"
            className="absolute w-[15px] text-[#8B969D] h-[15px] my-auto top-0 bottom-0 left-4"
          />
          <div
            className="!pl-[30px] w-full rounded-full border-[1.3px] border-gray-300 focus-within:border-river-blue 
            focus-within:ring-river-blue focus-within:ring-[0.3px]"
          >
            <Input
              name="name"
              placeholder="Patient name"
              value={search.name}
              autoComplete="off"
              type="search"
              className="!text-[15px] bg-white !py-[7px] rounded-full border-none focus:border-none focus:ring-transparent focus:ring-0"
              onChange={handleSearchChange}
            />
          </div>
        </div>
      )}

      {fields.includes('provider') && (
        <div className="flex-1">
          <SelectInput
            value={search.provider}
            name="provider"
            className="!text-[15px] !py-[6.8px] border-[1.3px] border-gray-300 rounded-full disabled:!bg-[#F2F4F5] disabled:border-[#dfe5eb]"
            placeholder={{
              value: '',
              label: 'Select Provider',
            }}
            options={[...[{ value: '', label: '-- select an option --' }], ...providers]}
            onChange={handleSearchChange}
          />
        </div>
      )}

      {fields.includes('state') && (
        <div className="w-[180px]">
          <SelectInput
            value={search.state}
            name="state"
            className="!text-[15px] !py-[6.8px] border-[1.3px] border-gray-300 rounded-full disabled:!bg-[#F2F4F5] disabled:border-[#dfe5eb]"
            placeholder={{
              value: '',
              label: 'State',
            }}
            options={[...[{ value: '', label: '-- select an option --' }], ...US_STATES]}
            onChange={handleSearchChange}
          />
        </div>
      )}

      {fields.includes('gender') && (
        <div className="w-[180px]">
          <SelectInput
            value={search.gender}
            name="gender"
            className="!text-[15px] !py-[6.8px] rounded-full disabled:!bg-[#F2F4F5] disabled:border-[#dfe5eb]"
            placeholder={{
              value: '',
              label: 'Gender',
            }}
            options={[...[{ value: '', label: '-- select an option --' }], ...GENDERS]}
            onChange={handleSearchChange}
          />
        </div>
      )}

      {fields.includes('month') && (
        <div className="w-[180px]">
          <SelectInput
            value={search.month}
            name="month"
            className="!text-[15px] !py-[6.8px] rounded-full disabled:!bg-[#F2F4F5] disabled:border-[#dfe5eb]"
            placeholder={{
              value: '',
              label: 'Month',
            }}
            options={[
              ...[{ value: '', label: '-- select an option --' }],
              ...moment.months().map(month => ({
                value: month,
                label: month,
              })),
            ]}
            onChange={handleSearchChange}
          />
        </div>
      )}

      {fields.includes('visit') && (
        <div className="w-[180px]">
          <SelectInput
            value={search.visit}
            name="visit"
            className="!text-[15px] !py-[6.8px] rounded-full disabled:!bg-[#F2F4F5] disabled:border-[#dfe5eb]"
            placeholder={{
              value: '',
              label: 'Visit Type',
            }}
            options={[
              ...[{ value: '', label: '-- select an option --' }],
              ...visitTypes.map(visit => ({
                value: visit.type,
                label: visit.name,
              })),
            ]}
            onChange={handleSearchChange}
          />
        </div>
      )}

      <button
        onClick={() => handleSearchViewClose()}
        type="button"
        className="hover:opacity-90 bg-river-black group/add rounded-full w-[38px] h-[38px] justify-center items-center flex"
      >
        <IconSprite id="icon-close" className="w-[18px] h-[18px] text-white" />
      </button>
    </div>
  );
}

function SearchFilterTag({
  tag,
  value,
  onTagClear,
}: {
  tag: string;
  value: string;
  onTagClear: (tag: string) => void;
}) {
  const parseTagValue = (fndValue: string, fndTag: string) => {
    if (fndTag === 'gender') {
      return GENDERS.find(fndGender => fndGender.value === fndValue)?.label || '';
    }

    if (fndTag === 'state') {
      return US_STATES.find(fndState => fndState.value === fndValue)?.label || '';
    }

    if (fndTag === 'visit') {
      return visitTypes.find(fndVisit => fndVisit.type === fndValue)?.name || '';
    }

    return fndValue;
  };

  return (
    <div
      className="flex flex-row items-center rounded-full border border-river-blue bg-[#E5F6FF] px-2 
      text-[13px] space-x-1 h-[32px]"
    >
      <span className="text-[#9DA4A8] font-light">{capitalize(tag)}:</span>
      <span className="font-medium whitespace-nowrap overflow-hidden text-ellipsis">
        {parseTagValue(value, tag)}
      </span>
      <button
        onClick={() => onTagClear(tag)}
        type="button"
        className="border- border-river-blue w-[16px] h-[16px] rounded-full inline-flex justify-center items-center 
        flex-shrink-0  hover:border hover:border-river-blue group !ml-2"
      >
        <Icon
          id="close"
          className="w-[8px] h-[8px] text-river-black stroke-1 group-hover:text-river-blue"
        />
      </button>
    </div>
  );
}

export { SearchFilter, SearchFilterTag };
